import {
  h,
  initializeWidgetConfiguration,
  SgWidget
} from '@sgwt-widget/core';
import { ComponentProps, emit, props } from 'skatejs';
import MiniFooter from './components/MiniFooter';
import * as css from './sgwt-mini-footer.less';
import { ISgwtMiniFooterProps } from './sgwt-mini-footer.types';
import { sgwtSkateProps } from '../common/SkateJsCustomProp';
import { openDidomiUserPreferences, getCurrentUserConsent, IUserConsent, runWhenDidomiIsReady } from './components/cookies-consent';
import { SgwtWidgetName, startWidgetMonitoring } from '../common/monitoring';

export class SgwtMiniFooter extends SgWidget<ISgwtMiniFooterProps> {
  public static is = 'sgwt-mini-footer';

  public static props: ComponentProps<SgwtMiniFooter, ISgwtMiniFooterProps> = {
    container: props.string,
    mode: props.string,
    contactUs: sgwtSkateProps.object,
    contactUsByHelpCenter: {...props.string, default: undefined},
    legalNotices: sgwtSkateProps.object,
    i18n: sgwtSkateProps.object,
    cookiesConsent: props.boolean,
    accessibilityCompliance: props.string,
    accessibilityLink: props.string,
  };

  constructor() {
    super();
  }

  public connectedCallback() {
    super.connectedCallback();
    emit(this, `${SgwtMiniFooter.is}--ready`);
    startWidgetMonitoring(SgwtWidgetName.MINI_FOOTER);
  }

  // Display the Didomi User Preferences popup
  public displayUserConsentPopup() {
    openDidomiUserPreferences();
  }

  // Get the current user consent
  public getCurrentUserConsent(): IUserConsent[] | null {
    return getCurrentUserConsent();
  }

  // Get the current user consent
  public async getCurrentUserConsentAsync(): Promise<IUserConsent[]> {
    return new Promise<IUserConsent[]>(resolve => {
      const consents: IUserConsent[] | null = getCurrentUserConsent();
      if (consents === null) {
        runWhenDidomiIsReady(() => {
          resolve(getCurrentUserConsent() || []);
        });
      } else {
        resolve(consents as IUserConsent[]);
      }
    });
  }

  // Get user content for a specific category.
  private getUserConsentForCategory(consents: IUserConsent[], category: 'web-analytics' | 'video'): boolean {
    // Keep only the consents on the dedicated category.
    const consentOnCategory: IUserConsent[] = consents.filter(c => c.category === category);
    // Get the consent from the Vendor ("Matomo") and the Purpose ("Video").
    const consentOnVendorLevel = consentOnCategory.some(c => c.type === 'vendor' && c.consent);
    const consentOnPurposeLevel = consentOnCategory.some(c => c.type === 'purpose' && c.consent);
    // The consent is done ONLY if the user gives the consent on the Puropose AND the Vendor
    return consentOnVendorLevel && consentOnPurposeLevel;
  }

  // Get the User Content for Matomo usage.
  public async getUserConsentOnMatomo(): Promise<boolean> {
    const consents = await this.getCurrentUserConsentAsync();
    return this.getUserConsentForCategory(consents, 'web-analytics');
  }

  // Get the User Content for Qumu usage.
  public async getUserConsentOnQumu(): Promise<boolean> {
    const consents = await this.getCurrentUserConsentAsync();
    return this.getUserConsentForCategory(consents, 'video');
  }

  private emitUserConsentEvent = (eventName: string, consents: IUserConsent[]) => {
    emit(this, `${SgwtMiniFooter.is}--${eventName}`, {
      detail: consents
    });
  }

  private isSGMarkets(): boolean {
    // Activated by mode="sg-markets" attribute
    if (!!this.props.mode && this.props.mode.toLowerCase() === 'sg-markets') {
      return true;
    }
    // Activated by the SG Markets domain
    const host = document.location.host.toLowerCase();
    return host.indexOf('.sgmarkets.com') > -1
      || host.indexOf('.sgmarkets-next.com') > -1
      || host.indexOf('.sgmarkets.world.socgen') > -1;
  }

  private isCookiesConsentEnabled() {
    // Forced by "cookies-consent" attribute
    if (this.props.cookiesConsent) {
      return true;
    }
    // Activated by mode="sg-markets" attribute
    return this.isSGMarkets();
  }

  private onAccessibilityClicked() {
    emit(this, `${SgwtMiniFooter.is}--accessibility-link-clicked`);
  }

  renderCallback({
    container,
    mode,
    contactUs,
    contactUsByHelpCenter,
    legalNotices,
    i18n,
    accessibilityCompliance,
    accessibilityLink
  }: ISgwtMiniFooterProps) {
    const cookiesConsentEnabled = this.isCookiesConsentEnabled();
    const sgmarkets = this.isSGMarkets();
    return (
      <span>
        <style>{css.toString()}</style>
        <MiniFooter
          container={container}
          additionalI18n={i18n}
          mode={mode}
          contactUs={contactUs}
          contactUsByHelpCenter={contactUsByHelpCenter}
          legalNotices={legalNotices}
          cookiesConsent={cookiesConsentEnabled}
          emitUserConsentEvent={this.emitUserConsentEvent}
          accessibilityCompliance={accessibilityCompliance}
          accessibilityLink={accessibilityLink}
          onAccessibilityClicked={() => this.onAccessibilityClicked()}
          isSGMarkets={sgmarkets}
        />
      </span>
    );
  }
}

initializeWidgetConfiguration(SgwtMiniFooter, {
  disablejQuerySanityCheck: true,
  neverUseShadowDOM: true
});
customElements.define(SgwtMiniFooter.is, SgwtMiniFooter);



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/sgwt-mini-footer/sgwt-mini-footer.tsx